import React from "react";

// Petak sawah isometrik 3×2 (SVG murni, proyeksi 2:1) — dipakai Scene (diam, semua kosong) dan mini game Sawah.
// Digambar ulang lebih besar: lahan rumput + parit irigasi, pematang tanah dengan rumpun rumput, petak cekung berisi air
// bergradasi + riak, rumpun padi 3×3 yang tumbuh, bulir emas merunduk saat siap, padi coklat saat layu, tanah retak saat kering.
// petak[i] = { fase: kosong|benih|basah|siap|layu|kering, hama: null|"tikus"|"burung", progres: 0..1, peringatan: bool }
export const KOLOM = 3, BARIS = 2, W = 150, H = 75;
export const KOSONG6 = Array.from({ length: KOLOM * BARIS }, () => ({ fase: "kosong" }));
const iso = (c, r) => [(c - r) * (W / 2), (c + r) * (H / 2)];
export const posisi = i => iso(i % KOLOM, Math.floor(i / KOLOM));
const diamond = (hw, hh) => `0,${-hh} ${hw},0 0,${hh} ${-hw},0`;
const KEDALAMAN = 6, INSET = 10;
const AIR = { kosong: ["#9ccbe0", "#6fa6c2"], benih: ["#9ccbe0", "#6fa6c2"], basah: ["#7fb3cd", "#5a90ad"], siap: ["#8db9c9", "#6a9bb2"], layu: ["#a9b7a4", "#86957f"] };
const LAHAN = [iso(-0.7, -0.7), iso(KOLOM - 0.3, -0.7), iso(KOLOM - 0.3, BARIS - 0.3), iso(-0.7, BARIS - 0.3)].map(p => p.join(",")).join(" ");
// posisi rumpun padi dalam satu petak (grid 3×3 iso kecil)
const RUMPUN = [-1, 0, 1].flatMap(c => [-1, 0, 1].map(r => [(c - r) * 20, (c + r) * 10]));

function Rumpun({ warna, tinggi, bengkok = 0, bulir, pucat }) {
  return RUMPUN.map(([x, y], i) => (
    <g key={i} transform={`translate(${x} ${y})`} opacity={pucat ? 0.7 : 1}>
      <ellipse rx="5" ry="2" fill="rgba(0,0,0,.12)" />
      {[-5, -2, 0, 2, 5].map((dx, k) => <path key={k} d={`M0 0 q${dx * 0.6 + bengkok} ${-tinggi * 0.5} ${dx + bengkok * 2} ${-tinggi * (0.85 + (k % 2) * 0.2)}`} stroke={warna} strokeWidth="2" fill="none" strokeLinecap="round" />)}
      {bulir && [-3, 2].map((dx, k) => <path key={`b${k}`} d={`M${dx} ${-tinggi} q6 2 7 9`} stroke="#e2b53c" strokeWidth="3.2" fill="none" strokeLinecap="round" strokeDasharray="1.5 1.8" />)}
    </g>
  ));
}

function Isi({ p }) {
  switch (p.fase) {
    case "benih": return <g>{RUMPUN.map(([x, y], i) => <g key={i} transform={`translate(${x} ${y})`}><ellipse rx="3.2" ry="2" fill="#6b4423" /><path d="M0 -1 v-6" stroke="#7fcf6a" strokeWidth="1.6" strokeLinecap="round" /></g>)}</g>;
    case "basah": return <Rumpun warna="#2e9e5b" tinggi={8 + (p.progres || 0) * 24} />;
    case "siap": return <g style={p.peringatan ? { animation: "jw-blink .5s steps(2) infinite" } : undefined}><Rumpun warna="#c9a227" tinggi={32} bulir /></g>;
    case "layu": return <Rumpun warna="#8a6a3c" tinggi={16} bengkok={7} />;
    case "kering": return <Rumpun warna="#9fae6a" tinggi={14} bengkok={3} pucat />;
    default: return <g opacity=".75"><circle r="16" fill="none" stroke="#fff" strokeWidth="1.6" strokeDasharray="4 4" /><path d="M0 8 V-2 M0 -2 q-8 -1 -9 -9 q9 0 9 9 M0 -2 q8 -1 9 -9 q-9 0 -9 9" stroke="#fff" strokeWidth="1.8" fill="none" strokeLinecap="round" /></g>;
  }
}

// Satu petak cekung: pematang → dinding belakang → dasar (air bergradasi + riak, atau tanah retak saat kering)
function Petak({ p, i }) {
  const hw = W / 2 - INSET, hh = H / 2 - INSET / 2, d = KEDALAMAN, kering = p.fase === "kering";
  const [a0, a1] = AIR[p.fase] || AIR.kosong;
  return (
    <>
      <polygon points={diamond(W / 2, H / 2)} fill="#a3784c" />
      <polygon points={diamond(W / 2, H / 2)} fill="none" stroke="#8d6640" strokeWidth="1.2" opacity=".7" />
      {[[-W / 2 + 14, 0], [W / 2 - 14, 0], [0, -H / 2 + 6], [0, H / 2 - 6]].map(([x, y], k) => <path key={k} d={`M${x - 4} ${y} q2 -6 4 -1 q2 -6 4 0`} stroke="#86bd66" strokeWidth="1.6" fill="none" strokeLinecap="round" />)}
      <polygon points={`0,${-hh} ${-hw},0 ${-hw},${d} 0,${-hh + d}`} fill="#5e4027" />
      <polygon points={`0,${-hh} ${hw},0 ${hw},${d} 0,${-hh + d}`} fill="#704d2e" />
      <g transform={`translate(0 ${d})`}>
        <defs><linearGradient id={`jw-air-${i}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={a0} /><stop offset="1" stopColor={a1} /></linearGradient></defs>
        <polygon points={diamond(hw, hh)} fill={kering ? "#b58a5a" : `url(#jw-air-${i})`} />
        {kering
          ? <path d="M-40 -4 l12 6 l10 -8 l14 10 l12 -6 M-20 12 l10 -6 l12 8 l14 -4 M-10 -16 l8 6 l10 -4" stroke="#7a5333" strokeWidth="1.6" fill="none" strokeLinecap="round" />
          : <>
            <ellipse cx="-14" cy="-8" rx="26" ry="6" fill="#fff" opacity=".14" />
            <ellipse cx="18" cy="8" rx="18" ry="4" fill="none" stroke="#fff" strokeWidth="1" opacity=".35" style={{ animation: `jw-riak 3.2s ${(i % 3) * 0.6}s ease-in-out infinite`, transformBox: "fill-box", transformOrigin: "center" }} />
          </>}
        <Isi p={p} />
      </g>
    </>
  );
}

function Tikus() {
  return (
    <g data-jenis="tikus" style={{ animation: "jw-hama .6s ease-in-out infinite", transformBox: "fill-box", transformOrigin: "center" }}>
      <ellipse cy="4" rx="16" ry="5" fill="rgba(0,0,0,.2)" />
      <path d="M14 -4 q14 -2 18 -12" stroke="#b5786a" strokeWidth="2.4" fill="none" strokeLinecap="round" />
      <ellipse cx="0" cy="-8" rx="17" ry="11" fill="#7d7d85" />
      <circle cx="-13" cy="-12" r="8" fill="#8d8d95" />
      <circle cx="-16" cy="-20" r="4.5" fill="#8d8d95" /><circle cx="-16" cy="-20" r="2.3" fill="#e8b4b0" />
      <circle cx="-9" cy="-19" r="4.5" fill="#8d8d95" /><circle cx="-9" cy="-19" r="2.3" fill="#e8b4b0" />
      <circle cx="-16" cy="-12" r="1.8" fill="#141414" /><circle cx="-20.5" cy="-10" r="1.8" fill="#2b1d16" />
      <path d="M-24 -11 h-6 M-24 -9 h-6" stroke="#141414" strokeWidth=".8" />
      <circle cx="-30" cy="-24" r="12" fill="none" stroke="#dc2626" strokeWidth="2.5" opacity=".9" /><path d="M-36 -24 h12 M-30 -30 v12" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" />
    </g>
  );
}

function Burung() {
  return (
    <g data-jenis="burung" style={{ animation: "jw-hama .4s ease-in-out infinite", transformBox: "fill-box", transformOrigin: "center" }}>
      <ellipse cy="2" rx="10" ry="3.5" fill="rgba(0,0,0,.2)" />
      <ellipse cx="0" cy="-12" rx="11" ry="8" fill="#8a5a3c" />
      <circle cx="9" cy="-18" r="6" fill="#a46e4a" /><path d="M14 -18 l7 2 l-7 2 Z" fill="#f2c230" />
      <circle cx="10.5" cy="-19" r="1.5" fill="#141414" />
      <path d="M-4 -14 q-8 -10 -14 -4 q6 2 10 8 Z" fill="#6e4523" style={{ animation: "jw-sayap .25s ease-in-out infinite alternate", transformBox: "fill-box", transformOrigin: "100% 100%" }} />
      <path d="M-11 -10 l-6 3 M-11 -8 l-6 5" stroke="#6e4523" strokeWidth="2" strokeLinecap="round" />
      <path d="M-2 -4 v6 M4 -4 v6" stroke="#f2c230" strokeWidth="1.6" />
      <circle cx="22" cy="-26" r="11" fill="none" stroke="#dc2626" strokeWidth="2.5" opacity=".9" /><path d="M16.5 -26 h11 M22 -31.5 v11" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" />
    </g>
  );
}

// Orang-orangan sawah (hiasan) di pojok lahan
function OrangOrangan({ x, y }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <ellipse rx="8" ry="3" fill="rgba(0,0,0,.15)" />
      <rect x="-1.5" y="-46" width="3" height="46" fill="#8a5a2b" /><rect x="-16" y="-36" width="32" height="3" fill="#8a5a2b" />
      <rect x="-9" y="-38" width="18" height="18" rx="3" fill="#b8352c" /><path d="M-9 -30 H9 M-9 -25 H9" stroke="rgba(0,0,0,.25)" strokeWidth="1.5" />
      <circle cy="-44" r="6.5" fill="#e9c489" /><path d="M-11 -46 L0 -56 L11 -46 Z" fill="#d9a857" /><ellipse cy="-46" rx="11" ry="2.5" fill="#c9954a" />
    </g>
  );
}

export function PetakSawah({ petak = KOSONG6, onTap, pops = [], style }) {
  const [px0, py0] = iso(-0.7, -0.7), [px1, py1] = iso(-0.7, BARIS - 0.3);
  return (
    <div data-testid="petak-sawah" style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)", width: "min(100vw, 560px)", pointerEvents: onTap ? "auto" : "none", ...style }}>
      <style>{`@keyframes jw-blink{0%,100%{opacity:1}50%{opacity:.45}}@keyframes jw-hama{0%,100%{transform:translateY(0)}50%{transform:translateY(-4px)}}
        @keyframes jw-sayap{0%{transform:rotate(-18deg)}100%{transform:rotate(14deg)}}@keyframes jw-riak{0%,100%{transform:scale(.85);opacity:.15}50%{transform:scale(1.1);opacity:.4}}
        @keyframes jw-popsvg{0%{opacity:1;transform:translateY(0)}100%{opacity:0;transform:translateY(-34px)}}`}</style>
      <svg viewBox="-192 -100 456 280" width="100%" style={{ display: "block", overflow: "visible" }}>
        {/* lahan rumput + pematang keliling + parit irigasi di sisi kiri */}
        <polygon points={LAHAN} fill="#9bd27a" stroke="#a3784c" strokeWidth="9" strokeLinejoin="round" />
        <polygon points={LAHAN} fill="none" stroke="#86bd66" strokeWidth="1.5" />
        <path d={`M${px0 - 14} ${py0 + 2} L${px1 - 14} ${py1 - 2}`} stroke="#6fa6c2" strokeWidth="9" strokeLinecap="round" />
        <path d={`M${px0 - 14} ${py0 + 2} L${px1 - 14} ${py1 - 2}`} stroke="#fff" strokeWidth="1.2" opacity=".4" strokeDasharray="6 10" />
        {petak.map((p, i) => {
          const [x, y] = posisi(i);
          return (
            <g key={i} data-testid={`petak-${i}`} data-fase={p.fase} data-hama={p.hama ? "1" : "0"} transform={`translate(${x} ${y})`} onPointerDown={onTap ? e => { e.preventDefault(); onTap(i); } : undefined} style={{ cursor: onTap ? "pointer" : "default" }}>
              <Petak p={p} i={i} />
              {p.hama && <g data-testid={`hama-${i}`} transform={`translate(0 ${KEDALAMAN - 6})`}>{p.hama === "burung" ? <Burung /> : <Tikus />}</g>}
            </g>
          );
        })}
        <OrangOrangan x={iso(KOLOM - 0.42, -0.55)[0]} y={iso(KOLOM - 0.42, -0.55)[1]} />
        {pops.map(pp => { const [x, y] = posisi(pp.i); return <text key={pp.k} x={x} y={y - 26} textAnchor="middle" fontSize="17" fontWeight="800" fill={pp.warna || "#fff"} stroke="rgba(0,0,0,.4)" strokeWidth=".7" paintOrder="stroke" style={{ animation: "jw-popsvg .8s ease-out forwards", transformBox: "fill-box", transformOrigin: "center", fontFamily: "inherit", pointerEvents: "none" }}>{pp.teks}</text>; })}
      </svg>
    </div>
  );
}
